import { styled } from '@mui/material';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipProps } from 'utils/helper';

const TYPES = {
    ai: ['feedback', 'grading', 'studentFeedback', 'assisted'],
    attempts: {
        includes: (value) => typeof value === 'number',
    },
    draft: {
        includes: (value) => typeof value === 'number',
    },
    dueDate: {
        includes: (value) => typeof value === 'number',
    },
    general: ['duplicate', 'graded', 'new', 'recommended', 'status', 'suggested', 'trial'],
    invitation: ['pending', 'declined', 'expired'],
    process: ['inProgress'],
    rating: ['agree', 'disagree', 'none'],
    role: ['instructor'],
    submission: ['submitted', 'late'],
    lms: ['lms'],
    preview: ['free'],
};

const CustomStack = styled(Stack)(({ size, type, value, theme }) => {
    const padding = {
        small: theme.spacing(0, 1),
        large: theme.spacing(1, 1.5),
    }[size];

    return {
        borderRadius: '4px',
        padding,
        ...(size === 'small' && {
            height: '1rem',
            ...(type === 'draft' && {
                display: 'inline-flex',
                borderRadius: '12px',
                padding: theme.spacing(1, 2),
                background: theme.palette.ensightful.neutral[700],
                color: theme.palette.ensightful.shades[0],
            }),
            ...(type === 'role' && {
                display: 'inline-flex',
                background: theme.palette.ensightful.neutral[800],
                color: theme.palette.ensightful.shades[0],
            }),
        }),
        ...(size === 'large' && {
            minWidth: '4.5rem',
            height: '1.5rem',
            ...(type === 'ai' && {
                borderRadius: '12px',
                width: 'fit-content',
                padding: theme.spacing(1, 2),
                ...(value === 'feedback' && {
                    background: theme.palette.ensightful.warning[200],
                    color: theme.palette.ensightful.warning[900],
                }),
                ...(value === 'grading' && {
                    background: theme.palette.ensightful.primary[100],
                    color: theme.palette.ensightful.primary[700],
                }),
                ...(value === 'studentFeedback' && {
                    background: theme.palette.ensightful.success[200],
                    color: theme.palette.ensightful.success[900],
                }),
                ...(value === 'assisted' && {
                    fontStyle: 'italic',
                    background: theme.palette.ensightful.secondary[500],
                    color: theme.palette.ensightful.shades[0],
                }),
            }),
            ...(type === 'attempts' && {
                width: 'fit-content',
                background: theme.palette.ensightful.primary[50],
                color: theme.palette.ensightful.primary[700],
            }),
            ...(type === 'dueDate' && {
                borderRadius: '12px',
                width: 'fit-content',
                padding: theme.spacing(1, 2),
                background: theme.palette.ensightful.primary[500],
                color: theme.palette.ensightful.neutral[50],
            }),
            ...(type === 'general' && {
                ...(value === 'duplicate' && {
                    borderRadius: '12px',
                    width: 'fit-content',
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
                ...(value === 'graded' && {
                    borderRadius: '12px',
                    background: theme.palette.ensightful.success[700],
                    color: theme.palette.ensightful.shades[0],
                }),
                ...(value === 'new' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
                ...(value === 'recommended' && {
                    background: theme.palette.ensightful.primary[50],
                    color: theme.palette.ensightful.primary[700],
                }),
                ...(value === 'status' && {
                    borderRadius: '12px',
                    width: 'fit-content',
                    minWidth: 0,
                    background: theme.palette.ensightful.neutral[200],
                    color: theme.palette.ensightful.neutral[900],
                }),
                ...(value === 'suggested' && {
                    background: theme.palette.ensightful.primary[50],
                    color: theme.palette.ensightful.primary[700],
                }),
                ...(value === 'trial' && {
                    borderRadius: '12px',
                    background: theme.palette.ensightful.success[700],
                    color: theme.palette.ensightful.shades[0],
                }),
            }),
            ...(type === 'invitation' && {
                ...(value === 'pending' && {
                    background: theme.palette.ensightful.neutral[100],
                    color: theme.palette.ensightful.neutral[800],
                }),
                ...(value === 'declined' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
                ...(value === 'expired' && {
                    background: theme.palette.ensightful.neutral[100],
                    color: theme.palette.ensightful.neutral[800],
                }),
            }),
            ...(type === 'process' && {
                ...(value === 'inProgress' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
            }),
            ...(type === 'rating' && {
                borderRadius: '12px',
                ...(value === 'agree' && {
                    background: theme.palette.ensightful.success[100],
                    color: theme.palette.ensightful.success[700],
                }),
                ...(value === 'disagree' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[700],
                }),
                ...(value === 'none' && {
                    background: theme.palette.ensightful.neutral[200],
                    color: theme.palette.ensightful.neutral[700],
                }),
            }),
            ...(type === 'submission' && {
                ...(value === 'submitted' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
                ...(value === 'late' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
            }),
            ...(type === 'lms' && {
                borderRadius: '12px',
                width: 'fit-content',
                padding: theme.spacing(1, 2),
                ...(value === 'lms' && {
                    background: theme.palette.ensightful.primary[500],
                    color: theme.palette.ensightful.shades[0],
                }),
            }),
            ...(type === 'preview' && {
                ...(value === 'free' && {
                    background: theme.palette.ensightful.primary[50],
                    color: theme.palette.ensightful.primary[700],
                }),
            }),
        }),
    };
});

const CustomIcon = styled(Icon)(({ kind, theme }) => {
    return {
        fontSize: '0.75rem',
        ...(kind === 'starFilled' && {
            color: theme.palette.ensightful.primary[50],
        }),
    };
});

const CustomText = styled(Text)(() => {
    return {
        marginTop: '1px',
    };
});

const getStartIcon = (type, value) => {
    if (type !== 'dueDate') {
        return {
            graded: <CustomIcon kind={'checkmark'} />,
            milestone: <CustomIcon kind={'starFilled'} />,
            submitted: <CustomIcon kind={'checkmark'} />,
        }[value];
    }

    return <CustomIcon kind={'clock'} />;
};

const getEndIcon = (type, value) => {
    if (type === 'rating') {
        return {
            agree: <CustomIcon kind={'thumbsUp'} />,
            disagree: <CustomIcon kind={'thumbsDown'} />,
        }[value];
    }

    return null;
};

const getText = (type, value, t) => {
    if (type === 'attempts')
        return t('assignment:attempts-remaining', '{{count}} attempts remaining', {
            count: value ?? 0,
        });
    if (type === 'draft') return `${t('status.draft', 'Draft')} ${value}`;
    if (type === 'dueDate') return t('common:ui.format-date-time', { val: value });
    return {
        // ai
        feedback: t('tag.submission-feedback', 'Submission Feedback'),
        grading: t('tag.submission-grading', 'Submission Grading'),
        studentFeedback: t('tag.instant-student-feedback', 'Instant Student Feedback'),
        assisted: t('tag.ai-assisted', 'AI-Assisted'),
        // general
        duplicate: t('tag.duplicate', 'Duplicate'),
        graded: t('tag.graded', 'Graded'),
        new: t('tag.new', 'New'),
        recommended: t('tag.recommended', 'Recommended'),
        status: t('tag.status', 'Status'),
        suggested: t('tag.suggested', 'Suggested'),
        trial: t('tag.trial', 'Free Trial'),
        // invitation
        pending: t('tag.pending', 'Pending'),
        declined: t('tag.declined', 'Declined'),
        expired: t('tag.invite-expired', 'Invite Expired'),
        // process
        inProgress: t('tag.in-progress', 'In Progress'),
        // rating
        agree: t('tag.marked-as-helpful', 'Marked as helpful'),
        disagree: t('tag.marked-as-not-helpful', 'Marked as unhelpful/incorrect'),
        none: t('tag.no-response', 'No response'),
        // role
        instructor: t('tag.instructor', 'Instructor'),
        // submission
        submitted: t('tag.submitted', 'Submitted'),
        late: t('tag.late', 'Late'),
        // LMS
        lms: t('tag.lms', 'LMS Assignment'),
        // preview
        free: t('tag.free', 'free'),
    }[value];
};

const BaseTag = forwardRef(({ component, size, type, value, ...rest }, ref) => {
    const { t } = useTranslation();
    const startIcon = getStartIcon(type, value);
    const text = getText(type, value, t);
    const endIcon = getEndIcon(type, value);

    return (
        <CustomStack
            ref={ref}
            alignItems={'center'}
            component={component}
            direction={'row'}
            justifyContent={'space-around'}
            size={size}
            spacing={1}
            type={type}
            value={value}
            {...rest}
        >
            {startIcon}
            <CustomText align={'center'} color={'inherit'} component={component || 'div'} variant={'p3'}>
                {text}
            </CustomText>
            {endIcon}
        </CustomStack>
    );
});

export default forwardRef(({ tooltip, type, value, ...rest }, ref) => {
    if (tooltip && !tooltip.component) {
        throw new Error(
            `Shell: Invalid tooltip in component Tag. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
        );
    }
    if (!Object.keys(TYPES).includes(type)) throw new Error(`Shell: Invalid type ${type} in component Tag.`);
    if (!TYPES[type].includes(value)) throw new Error(`Shell: Invalid value ${value} in component Tag.`);

    const TagComponent = (
        <BaseTag
            ref={ref}
            size={['draft', 'role'].includes(type) ? 'small' : 'large'}
            type={type}
            value={value}
            {...rest}
        />
    );

    return tooltip ? <Tooltip {...getTooltipProps(tooltip)}>{TagComponent}</Tooltip> : TagComponent;
});
